import React, { useEffect, useState } from 'react';

interface SurveyTerminationProps {
  reason?: string;
  redirectUrl?: string;
  message?: string;
}

/**
 * Component displayed when a survey session is terminated early.
 * Handles screened out users, quota full scenarios, and redirects to panel providers.
 */
export const SurveyTermination: React.FC<SurveyTerminationProps> = ({
  reason,
  redirectUrl,
  message
}) => {
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    if (!redirectUrl) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          window.location.href = redirectUrl;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [redirectUrl]);

  const getDisplayTitle = () => {
    switch (reason) {
      case 'screened_out':
        return 'Thank You for Your Interest';
      case 'quota_full':
        return 'Survey Closed';
      case 'automation_blocked':
        return 'Unsupported browser';
      default:
        return 'Survey Complete';
    }
  };

  const getDisplayMessage = () => {
    if (message) {
      return message;
    }

    switch (reason) {
      case 'completed':
        return 'You have already completed this survey. Thank you for your participation!';
      case 'screened_out':
        return 'Unfortunately, you do not meet the eligibility criteria for this study. We appreciate your time.';
      case 'quota_full':
        return 'Thank you for your interest. This survey has reached its maximum number of responses.';
      case 'automation_blocked':
        return "This survey can't be started in this browser. Please use a standard, up-to-date web browser (Chrome, Safari, Firefox, or Edge) without automation tools.";
      default:
        return 'This survey has ended. Thank you for your participation.';
    }
  };

  const handleRedirectNow = () => {
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        {/* Icon */}
        <div className="mb-6 flex justify-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          {getDisplayTitle()}
        </h2>

        {/* Message */}
        <p className="text-gray-600 mb-6 leading-relaxed">
          {getDisplayMessage()}
        </p>

        {/* Redirect Section */}
        {redirectUrl && (
          <div className="mt-6">
            <div className="mb-4 p-4 bg-blue-50 rounded-md">
              <p className="text-sm text-blue-800">
                Redirecting in{' '}
                <span className="font-bold text-lg">{countdown}</span> second
                {countdown !== 1 ? 's' : ''}...
              </p>
            </div>

            <button
              onClick={handleRedirectNow}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors font-medium"
            >
              Continue Now
            </button>
          </div>
        )}

        {/* No redirect - just close window option */}
        {!redirectUrl && (
          <div className="mt-6">
            <p className="text-sm text-gray-500">
              You may close this window.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};